import { useQuery } from '@tanstack/react-query'

import httpClient from '@/lib/httpClient.js'
import { TRANSACTION_TYPES } from '@/data/constants/constants'
import useAppStore from '@/store/useAppStore'
import useGetPeriodColumns from '@/services/api/cash-flow/useGetPeriodColumns'

type Period = { offset: number; start: string; end: string }

/**
 * Transaction detail for the dock list / model detail views.
 *
 * The grid feed carries only aggregates now, so transaction rows come from the
 * `index()` endpoint for the visible window (optionally scoped to one What-If
 * model). Rows are annotated with `offset` client-side by matching
 * `transaction_date` into the period columns, then the caller's filters apply.
 */
export default function useGetTransactionByFilter({
  offset,
  vaultId,
  categoryId,
  enabled = true,
  withInitialBalance = false,
  modelId,
  direction,
}: {
  offset?: string | number
  vaultId?: string | number
  categoryId?: string | number
  enabled?: boolean
  withInitialBalance?: boolean
  modelId?: string | number
  direction?: 'in' | 'out'
}) {
  const viewPeriodType = useAppStore((state) => state.periodType)
  const viewOffset = useAppStore((state) => state.offset)
  const viewNumberOfPeriods = useAppStore((state) => state.numberOfPeriods)

  const { data: periods } = useGetPeriodColumns({
    offset: viewOffset,
    numberOfPeriods: viewNumberOfPeriods,
    periodType: viewPeriodType,
  })

  const windowStart = periods?.[0]?.start
  const windowEnd = periods?.[periods.length - 1]?.end

  const offsetForDate = (date: string) => {
    const match = (periods as Period[] | undefined)?.find(
      (p) => date >= p.start && date <= p.end,
    )
    return match ? Number(match.offset) : undefined
  }

  const fetchData = async () => {
    const response = await httpClient.get(
      '/team/cash-flow/transactions/index',
      {
        params: {
          start: windowStart,
          end: windowEnd,
          ...(modelId ? { what_if_id: modelId } : {}),
        },
      },
    )

    const rows = (response.data.data ?? []).map((row: any) => ({
      ...row,
      offset: offsetForDate(row.transaction_date),
    }))

    return rows
      .filter((x: any) => (modelId ? x.what_if_id === Number(modelId) : true))
      .filter((x: any) =>
        withInitialBalance
          ? true
          : [TRANSACTION_TYPES.STANDARD, TRANSACTION_TYPES.TRANSFER].includes(
              x.transaction_type,
            ),
      )
      .filter((x: any) =>
        offset !== undefined && offset !== null && offset !== ''
          ? x.offset === Number(offset)
          : true,
      )
      .filter((x: any) => (vaultId ? x.vault_id === Number(vaultId) : true))
      .filter((x: any) =>
        categoryId ? x.transaction_category_id === Number(categoryId) : true,
      )
      .filter((x: any) => {
        if (!direction) return true
        // TransactionResource sets a numeric direction (1 / -1) on Standard rows.
        const wanted = direction === 'in' ? 1 : -1
        return x.direction === wanted || x.direction === (direction === 'in' ? 'MoneyIn' : 'MoneyOut')
      })
  }

  return useQuery({
    queryKey: [
      'transactions-cache',
      {
        offset,
        vaultId,
        categoryId,
        withInitialBalance,
        modelId,
        direction,
        viewPeriodType,
        viewOffset,
        viewNumberOfPeriods,
      },
    ],
    queryFn: fetchData,
    enabled: enabled && !!windowStart && !!windowEnd,
    placeholderData: [],
  })
}
